// Three pages, named for the question each one answers.
//
//   /              Overview     — "are you actually fast, next to everyone
//                                 else?". For a hotel or an executive. NO HOTEL
//                                 IS NAMED here: docs/GUARDRAILS.md §2 keeps
//                                 hotel names inside the company, and this is
//                                 the page most likely to be shown to one hotel
//                                 with other hotels' numbers on it.
//   /progress      Progress     — "are we getting faster or slower?". The
//                                 search results page over time. CON-50699.
//                                 Aggregate-only, for the same reason.
//   /diagnostics   Diagnostics  — "why is this score what it is?". Per hotel,
//                                 per page, per device. Names hotels. There is
//                                 no sign-in for now (decision 0020): anyone with
//                                 the link can open it, as they can every page.
//                                 A Vue page since 2026-10-05, sharing the
//                                 header, footer and design of the other two;
//                                 the hand-written original it replaced has
//                                 since been removed.
//
// "Standard & trend" and "Engineering" described the audience rather than the
// page — and "Standard" was the title of the Jira ticket, which meant something
// to the two people who had read it. Progress and Diagnostics say what you find
// there. Overview stays Overview: it is the landing page, and the word is
// exactly what it is.
//
// The old paths still work: /trend redirects here, and /dev is redirected by
// `web/app/public/_redirects` because the router never sees it. A bookmark
// should not be the thing that breaks.
//
// Lazy chunks: somebody opening the Overview to make an argument in a meeting
// should not download the diagnostics page's modals to do it.
import { createRouter, createWebHistory } from 'vue-router'
import { t, tk } from './lib/i18n.js'

/**
 * Pages the Vue app does NOT own, shown in the navigation as real links.
 *
 * Empty since Diagnostics became a Vue page. Kept as the place a static page
 * would be listed, because such a page must be a real `<a>`, never a
 * RouterLink: routing to it in the browser would render a Vue page that does
 * not exist instead of loading the real one.
 */
export const EXTERNAL_PAGES = []

export const ROUTES = [
  {
    path: '/',
    name: 'main',
    component: () => import('./pages/MainPage.vue'),
    meta: {
      title: tk('Overview'),
      nav: tk('Overview'),
      // Whether this page is allowed to render a hotel's name.
      // test/ui/guardrails.test.js walks the import graph of every page
      // marked false and fails if it reaches anything that fetches or prints
      // a hotel, so a new component cannot quietly leak one onto a page
      // shown to hotels.
      namesHotels: false,
    },
  },
  {
    path: '/progress',
    name: 'progress',
    component: () => import('./pages/TrendPage.vue'),
    meta: { title: tk('Progress over time'), nav: tk('Progress'), namesHotels: false },
  },
  {
    path: '/diagnostics',
    name: 'diagnostics',
    component: () => import('./pages/DiagnosticsPage.vue'),
    // The one page allowed to name a hotel. For staff; no sign-in yet (decision 0020).
    meta: { title: tk('Diagnostics'), nav: tk('Diagnostics'), namesHotels: true },
  },
]

/**
 * Old addresses, kept working.
 *
 * Client-side so the query string comes along: someone with
 * `/trend?period=daily&date=…` bookmarked lands on the same window. A
 * `_redirects` rule cannot do that, because Workers Assets does not carry query
 * parameters through a redirect rule.
 */
const MOVED = [{ path: '/trend', redirect: (to) => ({ path: '/progress', query: to.query }) }]

export function createAppRouter() {
  const router = createRouter({
    history: createWebHistory(),
    routes: [
      ...ROUTES,
      ...MOVED,
      // A stale link to a page that no longer exists should land somewhere
      // honest rather than on an empty shell.
      { path: '/:pathMatch(.*)*', redirect: '/' },
    ],
    scrollBehavior(to, from, saved) {
      // A modal opening or a window changing is a query change on the same
      // route. Scrolling to the top for that throws away the reader's place in
      // a long page, which is exactly what the dev page redesign is meant to
      // stop happening.
      if (to.path === from.path) return false
      // The first load: already at the top, or a reload's own place. Scrolling
      // there anyway forced a layout of the page in the middle of drawing it.
      if (!from.matched.length) return saved ?? false
      return saved ?? { top: 0 }
    },
  })

  router.afterEach((to) => {
    document.title = titleFor(to)
  })

  return router
}

/** The tab's title for a route, in the language on screen. */
export const titleFor = (route) =>
  route?.meta?.title ? `${t(route.meta.title)} — Tripla Performance Lab` : 'Tripla Performance Lab'
